'use client';

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Icon } from '@/components/ui/icon';
import type { VariantType } from '@/db/schema/enums';
import styles from './variant-selector.module.css';

interface VariantTab {
  variantType: VariantType;
  label: string;
  prepTimeMinutes: number;
  cookTimeMinutes: number;
  available: boolean; // false = variant not generated OR known infeasible
  /** True when the AI determined this variant cannot be produced. */
  notFeasible?: boolean;
  /** Human-readable reason; surfaced as a `title` tooltip on infeasible tabs. */
  notFeasibleReason?: string | null;
}

interface VariantSelectorProps {
  variants: VariantTab[];
  activeVariant: VariantType;
  onVariantChange: (variantType: VariantType) => void;
  /**
   * Optional — when provided, ungenerated (and NOT infeasible) variant tabs
   * render as "Generate" CTAs instead of disabled placeholders. Clicking
   * invokes this handler with the target variant. Any of the three variants
   * can be derived on demand — the user picks any of Quick/Classic/Bougie as
   * the anchor at recipe creation, so the remaining two are all derivable.
   *
   * The originRect is the bounding rect of the clicked tab — used by the
   * caller to anchor the "fly to notification bell" animation when the
   * action succeeds.
   */
  onGenerate?: (targetVariant: VariantType, originRect: DOMRect) => void;
  /** Variant currently being generated — renders a spinner and disables other CTAs. */
  generatingVariant?: VariantType | null;
  /** Variants with a server-known active derive_variant job. Tabs in this set
   *  render in the "Generating…" state across navigation, in addition to the
   *  transient `generatingVariant` set right after a click. */
  derivingVariants?: VariantType[];
}

const VARIANT_ORDER: VariantType[] = ['quick', 'classic', 'bougie'];

export function VariantSelector({
  variants,
  activeVariant,
  onVariantChange,
  onGenerate,
  generatingVariant = null,
  derivingVariants = [],
}: VariantSelectorProps) {
  const t = useTranslations();
  const tabListRef = useRef<HTMLDivElement>(null);
  const indicatorRef = useRef<HTMLDivElement>(null);

  // Slide the indicator to the active tab's position. Indicator colour is
  // driven entirely by CSS via `currentColor` + `data-variant` selectors on
  // the active tab — see variant-selector.module.css. No inline style needed.
  useEffect(() => {
    const bar = tabListRef.current;
    const ind = indicatorRef.current;
    if (!bar || !ind) return;
    const activeBtn = bar.querySelector<HTMLButtonElement>(`[data-variant="${activeVariant}"][data-active="true"]`);
    if (!activeBtn) return;
    const b = activeBtn.getBoundingClientRect();
    const p = bar.getBoundingClientRect();
    ind.style.width = `${b.width}px`;
    ind.style.transform = `translateX(${b.left - p.left}px)`;
  }, [activeVariant]);

  // Build full list including unavailable variants (gaps in the set)
  const allTabs: Array<VariantTab & { label: string }> = VARIANT_ORDER.map((type) => {
    const found = variants.find((v) => v.variantType === type);
    return {
      variantType: type,
      label: found?.label ?? t(`recipes.variants.${type}`),
      prepTimeMinutes: found?.prepTimeMinutes ?? 0,
      cookTimeMinutes: found?.cookTimeMinutes ?? 0,
      available: found?.available ?? false,
      notFeasible: found?.notFeasible ?? false,
      notFeasibleReason: found?.notFeasibleReason ?? null,
    };
  });

  // Keyboard navigation: arrow keys move between tabs.
  // Declared BEFORE the `hasAnyVariant` early return so the hook always
  // runs on every render (react-hooks/rules-of-hooks).
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
      const availableTabs = allTabs.filter((t) => t.available);
      const currentAvailableIndex = availableTabs.findIndex((t) => t.variantType === allTabs[index].variantType);

      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault();
        const direction = e.key === 'ArrowRight' ? 1 : -1;
        const nextIndex = (currentAvailableIndex + direction + availableTabs.length) % availableTabs.length;
        const nextTab = availableTabs[nextIndex];

        onVariantChange(nextTab.variantType);

        // Focus by data-variant attribute — positional index is unreliable when
        // Generate-CTA buttons are interspersed between available tabs in the DOM.
        const nextButton = tabListRef.current?.querySelector<HTMLButtonElement>(
          `[role="tab"][data-variant="${nextTab.variantType}"]`,
        );
        nextButton?.focus();
      }

      if (e.key === 'Home') {
        e.preventDefault();
        const firstAvailable = availableTabs[0];
        if (firstAvailable) {
          onVariantChange(firstAvailable.variantType);
          const firstButton = tabListRef.current?.querySelector<HTMLButtonElement>(
            `[role="tab"][data-variant="${firstAvailable.variantType}"]`,
          );
          firstButton?.focus();
        }
      }

      if (e.key === 'End') {
        e.preventDefault();
        const lastAvailable = availableTabs[availableTabs.length - 1];
        if (lastAvailable) {
          onVariantChange(lastAvailable.variantType);
          const lastButton = tabListRef.current?.querySelector<HTMLButtonElement>(
            `[role="tab"][data-variant="${lastAvailable.variantType}"]`,
          );
          lastButton?.focus();
        }
      }
    },
    [allTabs, onVariantChange],
  );

  // Hide the whole selector when nothing is generated AND nothing is
  // derivable (e.g. the only variant was infeasible). Otherwise render,
  // because the ungenerated slots now show as Generate CTAs.
  const hasAnyVariant = allTabs.some((t) => t.available);
  const hasGeneratableSlot =
    typeof onGenerate === 'function' &&
    allTabs.some((t) => !t.available && !t.notFeasible);
  if (!hasAnyVariant && !hasGeneratableSlot) return null;

  return (
    <div className={styles.wrapper}>
      <div
        ref={tabListRef}
        role="tablist"
        aria-label={t('recipes.variantSelectorAria')}
        className={styles.tabList}
      >
        <div ref={indicatorRef} className={styles.tabIndicator} aria-hidden="true" />
        {allTabs.map((tab, index) => {
          const isActive = tab.variantType === activeVariant;
          const totalTime = tab.prepTimeMinutes + tab.cookTimeMinutes;

          if (!tab.available) {
            // Infeasible variants stay disabled with an explainer — no amount
            // of generation will produce them (the AI already determined the
            // dish can't be e.g. Quick'd).
            if (tab.notFeasible) {
              const tooltip = tab.notFeasibleReason
                ? t('recipes.variantNotFeasibleTooltip', { reason: tab.notFeasibleReason })
                : t('recipes.variantNotFeasibleNoReason');

              return (
                <InfeasibleTab
                  key={tab.variantType}
                  variantType={tab.variantType}
                  label={tab.label}
                  tooltip={tooltip}
                  hasReason={Boolean(tab.notFeasibleReason)}
                  notFeasibleLabel={t('recipes.variantNotFeasible')}
                />
              );
            }

            // Ungenerated + not infeasible: render as an active Generate CTA
            // when onGenerate is provided. Any of Quick/Classic/Bougie can be
            // the anchor, so any missing non-infeasible variant is derivable.
            const canGenerate = typeof onGenerate === 'function';
            const isDeriving = derivingVariants.includes(tab.variantType);
            const isGeneratingThis =
              generatingVariant === tab.variantType || isDeriving;
            const isGeneratingOther = generatingVariant !== null && !isGeneratingThis;

            if (!canGenerate) {
              // Fallback: caller didn't wire onGenerate (e.g. logged-out viewer
              // of someone else's recipe). Show a plain disabled tab.
              return (
                <button
                  key={tab.variantType}
                  role="tab"
                  disabled
                  aria-selected={false}
                  aria-disabled={true}
                  className={styles.tab}
                  data-variant={tab.variantType}
                  title={t('recipes.variantNotGenerated')}
                  aria-label={t('recipes.variantNotGenerated')}
                >
                  <span className={styles.tabLabel}>{tab.label}</span>
                  <span className={styles.tabUnavailable}>{t('recipes.variantNotGenerated')}</span>
                </button>
              );
            }

            return (
              <button
                key={tab.variantType}
                role="tab"
                type="button"
                aria-selected={false}
                className={styles.tab}
                data-variant={tab.variantType}
                data-generate="true"
                data-generating={isGeneratingThis ? 'true' : undefined}
                disabled={isGeneratingThis || isGeneratingOther}
                onClick={(e) =>
                  onGenerate!(
                    tab.variantType,
                    (e.currentTarget as HTMLButtonElement).getBoundingClientRect(),
                  )
                }
                title={
                  isGeneratingThis
                    ? t('recipes.variantGeneratingTitle', { label: tab.label })
                    : t('recipes.variantGenerateTitle', { label: tab.label })
                }
                aria-label={
                  isGeneratingThis
                    ? t('recipes.variantGeneratingAria', { label: tab.label })
                    : t('recipes.variantGenerateAria', { label: tab.label })
                }
              >
                <span className={styles.tabLabel}>{tab.label}</span>
                <span className={styles.tabGenerate}>
                  {isGeneratingThis ? (
                    <>
                      <Icon
                        name="loading"
                        size={12}
                        strokeWidth={2}
                        className={styles.generateSpinner}
                      />
                      {isDeriving ? t('recipes.variantGenerating') : t('recipes.variantStarting')}
                    </>
                  ) : (
                    <>
                      <Icon
                        name="sparkles"
                        size={12}
                        strokeWidth={2}
                      />
                      {t('recipes.variantGenerate')}
                    </>
                  )}
                </span>
              </button>
            );
          }

          return (
            <button
              key={tab.variantType}
              id={`tab-${tab.variantType}`}
              role="tab"
              aria-selected={isActive}
              aria-controls={`tabpanel-${tab.variantType}`}
              tabIndex={isActive ? 0 : -1}
              className={styles.tab}
              data-variant={tab.variantType}
              data-active={String(isActive)}
              onClick={() => onVariantChange(tab.variantType)}
              onKeyDown={(e) => handleKeyDown(e, index)}
            >
              <span className={styles.tabLabel}>{tab.label}</span>
              {totalTime > 0 && (
                <span className={styles.tabMeta}>{t('recipes.minutes', { count: totalTime })}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// InfeasibleTab — replaces the disabled <button title="…"> with an accessible
// popover. Native `title` only shows on hover (mouse), is invisible to
// keyboard users, has zero styling control, and times out unpredictably.
//
// This wrapper:
//   - opens on hover (mouseenter), keyboard focus, and click/Enter
//   - closes on Escape, blur, mouseleave, and outside-click
//   - links the tooltip via `aria-describedby`, so screen readers announce
//     the reason as soon as the tab receives focus
//   - the tab keeps its tablist semantics (role=tab, aria-selected=false,
//     aria-disabled=true) but is not DOM-disabled — disabled buttons can't
//     receive focus on Safari, which would defeat the popover trigger.
// ---------------------------------------------------------------------------
function InfeasibleTab({
  variantType,
  label,
  tooltip,
  hasReason,
  notFeasibleLabel,
}: {
  variantType: VariantType;
  label: string;
  tooltip: string;
  hasReason: boolean;
  notFeasibleLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const tooltipId = useId();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onClick = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onClick);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  return (
    <span
      ref={wrapperRef}
      className={styles.infeasibleWrap}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        role="tab"
        aria-disabled
        aria-selected={false}
        aria-describedby={tooltipId}
        className={styles.tab}
        data-variant={variantType}
        data-infeasible="true"
        onClick={() => setOpen((v) => !v)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        <span className={styles.tabLabel}>{label}</span>
        <span className={styles.tabUnavailable}>
          {notFeasibleLabel}
          {hasReason && (
            <Icon
              name="info"
              size={12}
              strokeWidth={2}
              className={styles.infeasibleIcon}
            />
          )}
        </span>
      </button>
      <span
        id={tooltipId}
        role="tooltip"
        className={styles.infeasibleTooltip}
        data-open={open ? 'true' : 'false'}
      >
        {tooltip}
      </span>
    </span>
  );
}
